import { Search } from "components/DesignSystem/Search/Search";
import { ProductBody } from "components/product/ProductBody";
import {
  PRODUCT_BOOKS,
  PRODUCT_INTRAGROUP,
  PRODUCT_MAILROOM,
  PRODUCT_SALESTAX,
  PRODUCT_TAX,
  ACTIVE_SUBSCRIPTION,
} from "constants/billing";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import SmallTax from "static/images/SmallTax.svg";
import SmallBooks from "static/images/SmallBooks.svg";
import SmalMailroom from "static/images/SmallMailroom.svg";
import SmallSalesTax from "static/images/SmallSalesTax.svg";
import SmallIntragroup from "static/images/SmallIntragroup.svg";
import { ProductChip } from "components/product/ProductChip";
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { AddProductToCart } from "components/AddProductToCart/AddProductToCart";
import { CartModal } from "components/CartModal/CartModal";
import { PayDebt } from "components/PayDebt/PayDebt";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { parse } from "qs";
import { Button } from "components/DesignSystem/Button/Button";
import { PlusIcon } from "components/icons/PlusIcon";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import {
  CartTemplate,
  useGetCartTemplatesQuery,
  usePushToCartFromTemplateMutation,
} from "store/apis/products";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import Modal from "components/DesignSystem/Modal/Modal";
import { useRef, useState } from "react";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { TableUI } from "components/design/TableUI";
import classNames from "classnames";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { DividerLine } from "components/icons/DividerLine";
import { InfoFilledSmall } from "components/icons/InfoFilledSmall";
import { BILLING_CYCLE } from "dictionaries";
import ReactCountryFlag from "react-country-flag";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import ActiveStateIcon from "static/images/RegisteredStateIcon.svg";
import { VerticalDivider } from "components/design/VerticalDivider";
import { debounce } from "utils/debouncing";

type productQueryType =
  | typeof PRODUCT_BOOKS
  | typeof PRODUCT_TAX
  | typeof PRODUCT_INTRAGROUP
  | typeof PRODUCT_SALESTAX
  | typeof PRODUCT_MAILROOM
  | typeof ACTIVE_SUBSCRIPTION;

const TemplateItems = ({
  templateItems,
}: {
  templateItems: CartTemplate["items"];
}) => {
  const columnHelper = createColumnHelper<CartTemplate["items"][0]>();

  const columns = [
    columnHelper.accessor("product_details.product_name", {
      header: "Item",
      cell: (info) => {
        const {
          row: {
            original: {
              ra_state_name,
              tier_information,
              product_details: {
                season,
                has_balancing_payment,
                company_entity,
                state,
                product_name,
                slabs,
              },
            },
          },
        } = info;

        return (
          <div>
            <p className="t-m-0">{info.getValue()}</p>
            <p className="t-m-0 t-flex t-gap-1.5 t-items-center t-text-body-sm t-text-text-30">
              {company_entity && (
                <>
                  <ReactCountryFlag
                    countryCode={company_entity?.country_code}
                    svg
                  />
                  {company_entity?.name}{" "}
                </>
              )}
              {ra_state_name && <span>{ra_state_name}</span>}
              {tier_information && <span>{tier_information.range}</span>}
              {company_entity?.name && season && (
                <span className="t-text-neutral-20">
                  <DividerLine />
                </span>
              )}{" "}
              <span>{season}</span>
              {season && state && (
                <span className="t-text-neutral-20">
                  <DividerLine />
                </span>
              )}{" "}
              <span>{state?.name}</span>
            </p>
            {has_balancing_payment && (
              <p className="t-m-0 t-flex t-gap-1.5 t-items-center t-mt-1 t-text-body-sm">
                <span className="t-text-red t-flex">
                  <InfoFilledSmall />
                </span>
                <span>May have extra charges.</span>

                <Modal.Root>
                  <Modal.Trigger asChild>
                    {slabs && (
                      <button className="all:unset t-text-purple">
                        Learn more
                      </button>
                    )}
                  </Modal.Trigger>
                  <Modal.Content>
                    <Modal.Header>
                      <Modal.Title>{product_name}</Modal.Title>
                      <Modal.Close />
                    </Modal.Header>

                    <Modal.Body>
                      {slabs && (
                        <div
                          className="t-text-body-sm"
                          dangerouslySetInnerHTML={{
                            __html: slabs,
                          }}
                        />
                      )}
                    </Modal.Body>

                    <Modal.Footer>
                      <Modal.RawClose asChild>
                        <Button block>Go back to cart</Button>
                      </Modal.RawClose>
                    </Modal.Footer>
                  </Modal.Content>
                </Modal.Root>
              </p>
            )}
          </div>
        );
      },
      size: 70,
    }),

    columnHelper.accessor("quantity", {
      header: "QTY",
      size: 10,
    }),

    columnHelper.accessor("total", {
      header: "price",
      cell: (info) => {
        const { discount, subtotal } = info.row.original;

        return (
          <div className="">
            <p
              className={classNames("t-m-0", {
                "t-line-through t-text-body-sm t-text-text-30":
                  discount && discount.discount_value > 0,
              })}
            >
              <AmountSuperScript amount={subtotal} />
              {info.row.original.subscription && (
                <span className="t-text-text-30">
                  /
                  {BILLING_CYCLE[info.row.original.subscription?.billing_cycle]}
                </span>
              )}
            </p>
            {Boolean(discount) && discount && discount.discount_value > 0 && (
              <p className="t-m-0">
                <AmountSuperScript amount={info.getValue()} />
                {info.row.original.subscription && (
                  <span className="t-text-text-30">
                    /
                    {
                      BILLING_CYCLE[
                        info.row.original.subscription?.billing_cycle
                      ]
                    }
                  </span>
                )}
              </p>
            )}
          </div>
        );
      },
      size: 10,
    }),
  ];

  const table = useReactTable({
    columns,
    data: templateItems,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { minSize: 10 },
  });

  return <TableUI table={table} />;
};

export const Product = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { update } = useUpdateQuery();
  const query = useQuery();
  const { path, url } = useRouteMatch();
  const history = useHistory();
  const { link } = useConstructInternalLink();
  const isSubscriptionActive = query.get(ACTIVE_SUBSCRIPTION);
  const isBooksActive = query.get(PRODUCT_BOOKS);
  const isTaxActive = query.get(PRODUCT_TAX);
  const isMailroomActive = query.get(PRODUCT_MAILROOM);
  const isSalesTaxActive = query.get(PRODUCT_SALESTAX);
  const isIntragroupActive = query.get(PRODUCT_INTRAGROUP);
  const search = query.get("search") || "";
  const location = useLocation();
  const parsedSearch = parse(location.search, { ignoreQueryPrefix: true });
  const groupId = useCurrentGroupContext()?.uuid;
  const entityId = useCurrentEntityId();
  const [currentTemplateId, setCurrentTemplateId] = useState<string | null>();
  const { isAdmin, isPersonalTaxUser } = useRoleBasedView();

  const { data: cartTemplates } = useGetCartTemplatesQuery(
    {
      groupId,
      entityId,
    },
    {
      skip: !groupId || !entityId,
    }
  );

  const [addToCartFromTemplate, { isLoading: isAddingToCart }] =
    usePushToCartFromTemplateMutation();

  const onSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    update({ query: "search", value: e.target.value || null });
  }, 300);

  const scrollToTop = () => {
    wrapperRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const updateProductType = (
    query: productQueryType,
    value: boolean | null
  ) => {
    update({ query, value: value || null });
    if (value) scrollToTop();
  };

  const { successToast, alertToast } = useToast();

  const confirmAddToCart = async () => {
    if (!currentTemplateId) {
      return;
    }

    try {
      await addToCartFromTemplate({
        groupId: groupId!,
        entityId: entityId!,
        templateId: currentTemplateId,
      }).unwrap();
      setCurrentTemplateId(null);
      successToast({ title: "Template added to cart" });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <div ref={wrapperRef} className="t-mt-3">
      <div className="t-flex t-gap-8 t-justify-between t-sticky t-top-0 t-bg-white t-z-10 t-pb-5 sm:t-flex-col sm:t-gap-3">
        <div className="hide-scrollbar t-flex t-grow t-min-w-0 t-gap-2 t-items-center t-flex-wrap t-overflow-x-auto">
          <Search
            className="t-w-40 md:t-w-[18%] focus:t-w-3/6 t-transition-all t-shrink-0 sm:t-w-full sm:focus:t-w-full"
            onChange={onSearch}
            defaultValue={search}
            placeholder="Search"
          />
          <ProductChip
            onClick={() => updateProductType(ACTIVE_SUBSCRIPTION, true)}
            isActive={Boolean(isSubscriptionActive)}
            onClose={() => updateProductType(ACTIVE_SUBSCRIPTION, null)}
            icon={
              <img
                src={ActiveStateIcon}
                alt="ActiveStateIcon"
                className="t-h-4"
              />
            }
          >
            Active subscriptions
          </ProductChip>
          <div className="sm:t-hidden">
            <VerticalDivider />
          </div>
          <ProductChip
            onClick={() => updateProductType(PRODUCT_BOOKS, true)}
            isActive={Boolean(isBooksActive)}
            onClose={() => updateProductType(PRODUCT_BOOKS, null)}
            icon={<img src={SmallBooks} alt="SmallBooks" />}
          >
            Books
          </ProductChip>
          <ProductChip
            onClick={() => updateProductType(PRODUCT_TAX, true)}
            isActive={Boolean(isTaxActive)}
            onClose={() => updateProductType(PRODUCT_TAX, null)}
            icon={<img src={SmallTax} alt="SmallTax" />}
          >
            Tax
          </ProductChip>
          <ProductChip
            onClick={() => updateProductType(PRODUCT_MAILROOM, true)}
            isActive={Boolean(isMailroomActive)}
            onClose={() => updateProductType(PRODUCT_MAILROOM, null)}
            icon={<img src={SmalMailroom} alt="SmalMailroom" />}
          >
            Mailroom
          </ProductChip>
          <ProductChip
            onClick={() => updateProductType(PRODUCT_SALESTAX, true)}
            isActive={Boolean(isSalesTaxActive)}
            onClose={() => updateProductType(PRODUCT_SALESTAX, null)}
            icon={<img src={SmallSalesTax} alt="SmallSalesTax" />}
          >
            Sales Tax
          </ProductChip>
        </div>
        {isAdmin && (
          <Dropdown.Root>
            <Dropdown.Trigger asChild>
              <Button size="small" className="sm:t-self-start">
                <span className="t-flex t-gap-1">
                  <PlusIcon />
                  <span>Bulk add to cart</span>
                </span>
              </Button>
            </Dropdown.Trigger>
            <Dropdown.Content sideOffset={10} align="end">
              {cartTemplates?.map((template) => (
                <Dropdown.Item
                  key={template.uuid}
                  onSelect={() => setCurrentTemplateId(template.uuid)}
                >
                  {template.name}
                </Dropdown.Item>
              ))}
            </Dropdown.Content>
          </Dropdown.Root>
        )}
      </div>

      <ProductBody />
      <Switch>
        <Route exact path={`${path}/add-to-cart/:productId`}>
          <AddProductToCart
            hideEntity={isPersonalTaxUser}
            onClose={() =>
              history.push(link(url, { moreQuery: parsedSearch as {} }))
            }
          />
        </Route>
        <Route path={`${path}/cart`}>
          <CartModal
            open
            onClose={() =>
              history.push(link(url, { moreQuery: parsedSearch as {} }))
            }
          />
        </Route>
        <Route exact path={`${path}/payment-pending`}>
          <PayDebt
            isOpen={true}
            onClose={() =>
              history.push(link(url, { moreQuery: parsedSearch as {} }))
            }
          />
        </Route>
      </Switch>
      <Modal.Root
        open={Boolean(currentTemplateId)}
        onOpenChange={() => setCurrentTemplateId(null)}
      >
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Add template to cart</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <TemplateItems
              templateItems={
                cartTemplates?.find(
                  (template) => template.uuid === currentTemplateId
                )?.items || []
              }
            />
          </Modal.Body>

          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button>Cancel</Button>
            </Modal.RawClose>
            <Button
              customType="primary"
              onClick={confirmAddToCart}
              isLoading={isAddingToCart}
              disabled={isAddingToCart}
            >
              Add to cart
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
    </div>
  );
};
